import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Users, CheckCircle, XCircle, AlertCircle, LogIn, UserPlus } from 'lucide-react';
import { invitesApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const AcceptInvite = () => {
  const { inviteId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const { isDark } = useTheme();
  
  const [status, setStatus] = useState('loading'); // loading, requires_auth, accepting, accepted, error
  const [errorMessage, setErrorMessage] = useState('');
  const [inviteInfo, setInviteInfo] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated) {
      // Store invite ID for after login
      sessionStorage.setItem('pendingInviteId', inviteId);
      setStatus('requires_auth');
    } else {
      // User is logged in, try to accept the invite
      acceptInvite();
    }
  }, [authLoading, isAuthenticated, inviteId]);

  const acceptInvite = async () => {
    setStatus('accepting');
    try {
      const response = await invitesApi.accept(inviteId);
      setInviteInfo(response.data);
      setStatus('accepted');
      sessionStorage.removeItem('pendingInviteId');
      toast.success('Successfully joined the team!');
      
      // Redirect to team after a short delay
      setTimeout(() => {
        navigate(`/teams/${response.data.team_id}`);
      }, 2000);
    } catch (error) {
      setStatus('error');
      const detail = error.response?.data?.detail || 'Failed to accept invitation';
      setErrorMessage(detail);
      
      // If invite not found or already accepted, clear session storage
      sessionStorage.removeItem('pendingInviteId');
    }
  };

  const bgClass = isDark ? 'bg-[#0A0A0A]' : 'bg-[#F5F2EA]';
  const cardClass = isDark 
    ? 'bg-white dark:bg-[#111827] border-slate-200 dark:border-[#1F2937]' 
    : 'bg-white border-[#EBE9E1]';
  const textClass = isDark ? 'text-slate-800 dark:text-slate-100' : 'text-slate-800';
  const mutedTextClass = isDark ? 'text-slate-500 dark:text-slate-400' : 'text-slate-600';

  return (
    <div className={`min-h-screen ${bgClass} flex items-center justify-center p-4`}>
      <div className={`max-w-md w-full ${cardClass} border rounded-2xl p-8 text-center`}>
        {/* Logo */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6] bg-clip-text text-transparent" style={{ fontFamily: 'Outfit' }}>
            Strategic Architect
          </h1>
          <p className={`text-sm ${mutedTextClass} mt-1`}>Team Invitation</p>
        </div>

        {/* Loading State */}
        {(status === 'loading' || status === 'accepting') && (
          <div className="py-8" data-testid="invite-loading">
            <div className={`w-12 h-12 mx-auto border-3 rounded-full animate-spin ${isDark ? 'border-slate-200 dark:border-[#1F2937] border-t-[#26C6DA]' : 'border-[#EBE9E1] border-t-[#0F5156]'}`} />
            <p className={`mt-4 ${mutedTextClass}`}>
              {status === 'accepting' ? 'Joining team...' : 'Loading invitation...'}
            </p>
          </div>
        )}

        {/* Requires Auth State */}
        {status === 'requires_auth' && (
          <div className="py-6" data-testid="invite-requires-auth">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#26C6DA]/10 flex items-center justify-center">
              <Users className="w-8 h-8 text-[#26C6DA]" />
            </div>
            <h2 className={`text-xl font-semibold ${textClass} mb-2`} style={{ fontFamily: 'Outfit' }}>
              You've been invited to join a team!
            </h2>
            <p className={`${mutedTextClass} mb-8`}>
              Please sign in or create an account to accept this invitation.
            </p>
            
            <div className="space-y-3">
              <Link to="/login" className="block">
                <Button className="w-full bg-[#26C6DA] hover:bg-[#0891B2] text-white rounded-full gap-2" data-testid="invite-login-btn">
                  <LogIn className="w-4 h-4" />
                  Sign In
                </Button>
              </Link>
              <Link to="/register" className="block">
                <Button variant="outline" className="w-full rounded-full gap-2" data-testid="invite-register-btn">
                  <UserPlus className="w-4 h-4" />
                  Create Account
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* Accepted State */}
        {status === 'accepted' && (
          <div className="py-6" data-testid="invite-accepted">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-emerald-500/10 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-emerald-500" />
            </div>
            <h2 className={`text-xl font-semibold ${textClass} mb-2`} style={{ fontFamily: 'Outfit' }}>
              Welcome to the team!
            </h2>
            <p className={`${mutedTextClass} mb-6`}>
              You've successfully joined{inviteInfo?.team_name ? ` ${inviteInfo.team_name}` : ' the team'}.
            </p>
            <p className={`text-sm ${mutedTextClass}`}>
              Redirecting you to the team page...
            </p>
          </div>
        )}

        {/* Error State */}
        {status === 'error' && (
          <div className="py-6" data-testid="invite-error">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/10 flex items-center justify-center">
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className={`text-xl font-semibold ${textClass} mb-2`} style={{ fontFamily: 'Outfit' }}>
              Unable to accept invitation
            </h2>
            <p className={`${mutedTextClass} mb-6`}>
              {errorMessage}
            </p>
            
            <div className="space-y-3">
              <Link to="/teams" className="block">
                <Button className="w-full bg-[#26C6DA] hover:bg-[#0891B2] text-white rounded-full" data-testid="invite-go-teams-btn">
                  Go to My Teams
                </Button>
              </Link>
              <Link to="/dashboard" className="block">
                <Button variant="outline" className="w-full rounded-full">
                  Go to Dashboard
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcceptInvite;
